/**
 * Targets.
 *
 * Two audiences on one screen, which is why it branches on role rather than
 * being two files:
 *
 *   A salesperson sees his own daily and monthly target and how far along he
 *   is. Read-only — targets are set by the office, as agreed.
 *
 *   An admin picks a salesperson and sets one. All three measures are offered
 *   (cartons, value, customer count) and at least one must be non-zero; the
 *   server rejects an all-zero target rather than storing a meaningless row.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Page } from '../../components/Shell';
import {
  Banner, Button, Card, Chip, Empty, ErrorState, Segment, Sheet,
  Skeleton, TextArea, TextInput, Tile, useToast,
} from '../../components/ui';
import { useQuery } from '../../lib/useApi';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { useT } from '../../lib/i18n';
import { money, businessDate } from '../../lib/format';

interface TargetRow {
  id: string; dimension_type: string;
  category_id?: string | null;
  target_boxes: number;
  target_quantity: number; target_value_paise: number; target_customer_count: number;
  category_name: string | null; area_name: string | null; store_type_name: string | null;
  /**
   * What was achieved AGAINST THIS TARGET, on its own dimension.
   *
   * Every row used to display the block's headline figures instead, so a
   * category target read as met the moment the salesperson sold that much of
   * anything — and showed the identical percentage to the overall target
   * beside it. Per-row numbers are the whole point of a dimensioned target.
   */
  achieved: { boxes: number; quantity: number; valuePaise: number; customerCount: number };
  achievementPercent: {
    boxes: number | null; quantity: number | null;
    value: number | null; customers: number | null };
}

interface TargetBlock {
  period: string;
  targets: TargetRow[];
  achieved: { boxes: number; quantity: number; valuePaise: number; customerCount: number };
  achievementPercent: {
    boxes: number | null; quantity: number | null;
    value: number | null; customers: number | null } | null;
}

/** A driver's month: one number, split into the half he controls and the half he does not. */
interface DriverTargets {
  month: string;
  date: string;
  targetBoxes: number;
  dailyTargetBoxes: number;
  monthly: { delivered: number; sold: number; totalBoxes: number; percent: number | null };
  today: { delivered: number; sold: number; totalBoxes: number; percent: number | null };
}

export default function Targets() {
  const t = useT();
  const { user } = useAuth();

  const isAdmin = user?.role === 'owner_admin' || user?.role === 'manager';

  // Deep link from "set their target now" after a seller is registered —
  // ?salesperson=<id> pre-selects that person so the admin lands ready to set it.
  const [params] = useSearchParams();
  const linkedSalespersonId = params.get('salesperson');

  const [salespersonId, setSalespersonId] = useState(
    (isAdmin && linkedSalespersonId) || user?.salespersonId || '',
  );

  const { data: staff } = useQuery<{
    salespersons: { id: string; full_name: string; salesperson_code: string; is_active: number }[];
    drivers: { id: string; full_name: string; driver_code: string; is_active: number }[];
  }>(isAdmin ? '/api/admin/staff' : null);

  /**
   * One picker for both kinds of person.
   *
   * Drivers got targets on 6 Oct. A second dropdown beside the first would
   * leave an admin able to choose both, and the server refuses a target that
   * belongs to two people — so the choice is made once, here, and the value
   * carries which kind it is.
   */
  const [whoKind, setWhoKind] = useState<'salesperson' | 'driver'>('salesperson');
  const [driverId, setDriverId] = useState('');
  const who = whoKind === 'driver' ? driverId : salespersonId;

  const { data, loading, error, refetch } = useQuery<{
    driver: DriverTargets | null;
    daily: TargetBlock | null;
    monthly: TargetBlock | null;
  }>(
    who
      ? `/api/field/targets?${whoKind === 'driver' ? 'driverId' : 'salespersonId'}=${who}`
      : null,
    { deps: [who, whoKind] },
  );

  const [setting, setSetting] = useState(false);
  // Monthly / Today, mirroring the dashboard's "Performance & targets" toggle.
  const [win, setWin] = useState<'month' | 'today'>('today');

  // The field user's own day, for the summary tiles. Not fetched for an admin
  // (who gets no tiles) and not for the admin viewing someone else.
  const isField = user?.role === 'salesperson' || user?.role === 'driver' || user?.role === 'field_agent';
  // A field_agent runs a driver-style day (van + deliveries), so the driver view.
  const isDriverView = user?.role === 'driver' || user?.role === 'field_agent';
  const dayQ = useQuery<{
    progress?: {
      outletsVisited?: number; collectionsAmountPaise?: number; ordersBooked?: number;
    };
  }>(isField && !isAdmin ? '/api/field/day/current' : null);

  // The block in view, by window.
  const block = win === 'month' ? data?.monthly : data?.daily;

  return (
    <Page title={t('target.title')} back wide>
      <div className="stack">
        {isAdmin && (
          <>
            <Segment<'salesperson' | 'driver'>
              value={whoKind}
              onChange={(v) => { setWhoKind(v); setSetting(false); }}
              options={[
                { value: 'salesperson', label: 'Salesperson' },
                { value: 'driver', label: 'Driver' },
              ]}
            />
            <div className="field">
              <label>{whoKind === 'driver' ? 'Driver' : 'Salesperson'}</label>
              <select
                className="select"
                value={who}
                onChange={(e) => (whoKind === 'driver'
                  ? setDriverId(e.target.value)
                  : setSalespersonId(e.target.value))}
                aria-label={whoKind === 'driver' ? 'Driver' : 'Salesperson'}
              >
                <option value="">Choose a {whoKind}</option>
                {/*
                  No client filter. `/api/admin/staff` returns only people
                  who are available — both their profile and their login —
                  unless asked for everyone. Filtering here as well meant
                  six screens each deciding, on whichever of the two columns
                  the author happened to pick.
                */}
                {whoKind === 'driver'
                  ? (staff?.drivers ?? []).map((d) => (
                    <option key={d.id} value={d.id}>{d.full_name} ({d.driver_code})</option>
                  ))
                  : (staff?.salespersons ?? []).map((sp) => (
                    <option key={sp.id} value={sp.id}>
                      {sp.full_name} ({sp.salesperson_code})
                    </option>
                  ))}
              </select>
            </div>
          </>
        )}

        {!who && (
          <Empty
            title={isAdmin ? `Pick a ${whoKind}` : 'No field profile'}
            hint={isAdmin ? undefined : 'Targets apply to salespersons and drivers.'}
          />
        )}

        {who && loading && !data && <Skeleton rows={3} />}
        {error && <ErrorState message={error.message} onRetry={refetch} />}

        {/* Monthly / Today — one toggle for everyone, like the dashboard's
            Performance & targets section. */}
        {who && data && (data.driver || data.monthly !== undefined) && (
          <Segment<'month' | 'today'>
            value={win}
            onChange={setWin}
            options={[
              { value: 'month', label: t('target.monthly') },
              { value: 'today', label: "Today's target" },
            ]}
          />
        )}

        {/* Role tiles — the field user's own live figures. Hidden for the admin
            managing someone else's targets. Salespeople see three; drivers get
            "Orders delivered" as a fourth. */}
        {!isAdmin && isField && data && (
          <div className="grid-auto">
            <Tile
              tone="amber"
              label="Pets sold"
              value={data.driver
                ? (win === 'month' ? data.driver.monthly.totalBoxes : data.driver.today.totalBoxes)
                : (win === 'month' ? data.monthly : data.daily)?.achieved.boxes ?? 0}
            />
            <Tile
              tone="blue"
              label="Customers served"
              value={dayQ.data?.progress?.outletsVisited
                ?? (win === 'month' ? data.monthly : data.daily)?.achieved.customerCount ?? 0}
            />
            {isDriverView && (
              <Tile
                tone="purple"
                label="Orders delivered"
                value={data.driver
                  ? (win === 'month' ? data.driver.monthly.delivered : data.driver.today.delivered)
                  : 0}
              />
            )}
            <Tile tone="green" label="Collections" value={money(dayQ.data?.progress?.collectionsAmountPaise ?? 0)} />
          </div>
        )}

        {/* Four standard categories, in PETs, for the selected window. */}
        {data?.driver && <DriverBlock d={data.driver} win={win} />}
        {data?.driver === null && block !== undefined && (
          <CategoryTargets block={block} />
        )}
        {who && whoKind === 'driver' && data && !data.driver && (
          <Banner tone="info" title="No target set for this month">
            <span className="t-sm">Set one below and it appears on his phone straight away.</span>
          </Banner>
        )}

        {isAdmin && who && (
          <Button block onClick={() => setSetting(true)}>Set a target</Button>
        )}

        {!isAdmin && (
          <div className="t-xs t-muted">
            Targets are set by the office. Speak to your manager if one looks wrong.
          </div>
        )}
      </div>

      {setting && (
        <SetTarget
          who={{ kind: whoKind, id: who }}
          existing={data?.monthly ?? null}
          onClose={() => setSetting(false)}
          onDone={async () => { setSetting(false); await refetch(); }}
        />
      )}
    </Page>
  );
}


/**
 * A driver's month: one target, two halves.
 *
 * The client asked for delivered and sold "shown separately" (6 Oct). They
 * are added for the percentage — the target is one number — but printed
 * apart, because only one of them is his doing. A driver given a heavy route
 * and selling nothing all month would otherwise read as a star.
 */
function DriverBlock({ d, win }: { d: DriverTargets; win: 'month' | 'today' }) {
  const row = (label: string, value: number, hint?: string) => (
    <div className="row-between">
      <span className="t-sm t-muted">
        {label}
        {hint && <span className="t-xs t-faint"> · {hint}</span>}
      </span>
      <span className="t-sm t-semi">{value} PET{value === 1 ? '' : 's'}</span>
    </div>
  );

  // A driver's target is a single PET (box) figure, not split by category, so
  // the window chooses between the month and the month ÷ 30.
  const m = win === 'month';
  const stats = m ? d.monthly : d.today;
  const target = m ? d.targetBoxes : d.dailyTargetBoxes;

  return (
    <Card className="stack-2">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="t-semi">{m ? 'This month' : 'Today'}</span>
        <span className="t-sm t-muted">{m ? d.month : d.date}</span>
      </div>

      <Line
        label="PETs"
        achieved={String(stats.totalBoxes)}
        target={String(target)}
        percent={stats.percent ?? 0}
      />
      {row('Delivered', stats.delivered, 'routed to him')}
      {row('Sold off the van', stats.sold, 'his own')}
      {!m && (
        <div className="t-xs t-faint">
          {d.targetBoxes} PETs this month ÷ 30 days = {d.dailyTargetBoxes} a day.
        </div>
      )}
    </Card>
  );
}

/**
 * The four standard product groups, matched to a category target by name — the
 * same set the dashboard's category reach uses, so the two screens agree.
 */
const STD_CATS: { name: string; re: RegExp }[] = [
  { name: 'Campa Water', re: /water/i },
  { name: 'Cold Drink', re: /cola|cold|soft/i },
  { name: 'Juice', re: /juice/i },
  // `emergy` too: Business Central has the category misspelled "EMERGY DRINK",
  // and a target saved against it must still find this bar.
  { name: 'Energy Drink', re: /energy|emergy/i },
];

const barTone = (pct: number) => (pct >= 100 ? 'ok' : pct >= 70 ? 'warn' : 'danger');

/**
 * Target progress by the four standard categories, in PETs. Every category is
 * shown whether or not a target is set — an unset one reads "—", so the screen
 * never hides a category and never invents a goal.
 */
function CategoryTargets({ block }: { block: TargetBlock | null }) {
  if (!block) {
    return (
      <Banner tone="info" title="No target set">
        <span className="t-sm">No target exists for this period.</span>
      </Banner>
    );
  }

  const overall = block.targets.find((tr) => tr.dimension_type === 'overall' && tr.target_boxes > 0);
  const catTargets = block.targets.filter((tr) => tr.dimension_type === 'category');
  const rows = STD_CATS.map((c) => {
    // The real target wins if older empty duplicates linger: take the highest
    // target_boxes among the rows matching this category.
    const hit = catTargets
      .filter((tr) => tr.category_name && c.re.test(tr.category_name))
      .sort((a, b) => b.target_boxes - a.target_boxes)[0];
    return {
      name: c.name,
      achieved: hit?.achieved.boxes ?? 0,
      target: hit?.target_boxes ?? 0,
      percent: hit?.achievementPercent.boxes ?? 0,
      set: !!hit,
    };
  });

  return (
    <Card className="stack-2">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <span className="t-semi">Target · PETs by category</span>
        <span className="t-sm t-muted">{block.period}</span>
      </div>

      {overall && (
        <Line
          label="Overall"
          achieved={`${overall.achieved.boxes} PETs`}
          target={`${overall.target_boxes} PETs`}
          percent={overall.achievementPercent.boxes ?? 0}
        />
      )}

      {rows.map((r) => (
        <div key={r.name}>
          <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
            <span className="t-sm t-muted">{r.name}</span>
            <span className="t-sm">
              <span className="t-semi">{r.achieved}</span>
              <span className="t-muted"> / {r.set ? `${r.target} PETs` : '—'}</span>
              {r.set && <span className={`t-semi t-${barTone(r.percent)}`}> · {r.percent}%</span>}
            </span>
          </div>
          <div className="meter" role="progressbar" aria-valuenow={r.percent} aria-valuemin={0} aria-valuemax={100}>
            <div
              className={`meter__fill meter__fill--${barTone(r.percent)}`}
              style={{ width: `${r.set ? Math.min(100, Math.max(0, r.percent)) : 0}%` }}
            />
          </div>
        </div>
      ))}
    </Card>
  );
}

function Line({
  label, achieved, target, percent: pct,
}: {
  label: string; achieved: string; target: string; percent: number;
}) {
  const tone = pct >= 100 ? 'ok' : pct >= 70 ? 'warn' : 'danger';
  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 4 }}>
        <span className="t-sm t-muted">{label}</span>
        <span className="t-sm">
          <span className="t-semi">{achieved}</span>
          <span className="t-muted"> / {target}</span>
          <span className={`t-semi t-${tone}`}> · {pct}%</span>
        </span>
      </div>
      <div className="meter" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div
          className={`meter__fill meter__fill--${tone}`}
          style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Setting a target
// ---------------------------------------------------------------------------

function SetTarget({
  who, existing, onClose, onDone,
}: {
  who: { kind: 'salesperson' | 'driver'; id: string };
  existing: TargetBlock | null;
  onClose: () => void;
  onDone: () => void | Promise<void>;
}) {
  const t = useT();
  const toast = useToast();

  const today = businessDate();
  /**
   * SEVERAL CATEGORIES AT ONCE, each with its own figure.
   *
   * Asked for 4 Oct: "admin can select at a time only 1 category but we have
   * to give option to select multiple category." Setting a quarter's targets
   * meant opening this sheet once per product group and retyping the period
   * and the salesperson every time.
   *
   * Each selection becomes its OWN target, measured against its own
   * category — not one target spanning several, which would be met by
   * selling all of it in whichever category is easiest. Keyed by category id
   * so a figure typed and then deselected does not come back if the category
   * is picked again.
   */
  const [picked, setPicked] = useState<Record<string, { boxes: string; customers: string }>>({});

  /**
   * Categories come from Business Central's Item Category Code.
   *
   * Only ones with products in them: an empty category in this picker is a
   * target somebody can set and never achieve, and it would read as the
   * salesperson's failure rather than as a gap in the item master.
   */
  const categories = useQuery<{
    items: { id: string; name: string; code: string | null; product_count: number }[];
  }>('/api/field/targets/categories');
  const [month, setMonth] = useState(today.slice(0, 7));
  const [day, setDay] = useState(today);
  /** Boxes, not rupees — 6 Oct. A string so the field can be left blank. */
  const [boxes, setBoxes] = useState('');
  const [customers, setCustomers] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  // Pre-fill from the targets already set for this person/month, so the sheet
  // EDITS what exists instead of starting blank. A category row pre-selects its
  // chip and fills its box/customer figures; an overall target fills the two
  // top fields. Saving replaces (the server upserts), so editing is seamless.
  useEffect(() => {
    if (!existing) return;
    const next: Record<string, { boxes: string; customers: string }> = {};
    for (const tr of existing.targets) {
      if (tr.dimension_type === 'category' && tr.category_id) {
        // Don't let an older empty duplicate overwrite a real figure.
        if (next[tr.category_id] && tr.target_boxes === 0 && tr.target_customer_count === 0) continue;
        next[tr.category_id] = {
          boxes: tr.target_boxes > 0 ? String(tr.target_boxes) : '',
          customers: tr.target_customer_count > 0 ? String(tr.target_customer_count) : '',
        };
      } else if (tr.dimension_type === 'overall') {
        if (tr.target_boxes > 0) setBoxes(String(tr.target_boxes));
        if (tr.target_customer_count > 0) setCustomers(String(tr.target_customer_count));
      }
    }
    if (Object.keys(next).length > 0) setPicked(next);
    // Only on first open — further edits are the user's.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const chosen = Object.keys(picked);

  const submit = async () => {
    /**
     * One request per target, because one target is what each row IS.
     *
     * A bulk endpoint would have to decide what happens when the third of
     * five is rejected, and whatever it decided would be invisible from
     * here. Sending them one at a time means the failure names the category
     * it belongs to and the ones that worked stay set.
     */
    const jobs = chosen.length > 0
      ? chosen.map((id) => ({
        categoryId: id,
        name: categories.data?.items.find((c) => c.id === id)?.name ?? 'category',
        boxes: parseInt(picked[id]!.boxes || '0', 10),
        cust: parseInt(picked[id]!.customers || '0', 10),
      }))
      : [{
        categoryId: '',
        name: 'overall',
        boxes: parseInt(boxes || '0', 10),
        cust: parseInt(customers || '0', 10),
      }];

    const empty = jobs.filter((j) => !j.boxes && !j.cust);
    if (empty.length > 0) {
      toast.show(
        chosen.length > 0
          ? `Give ${empty.map((j) => j.name).join(', ')} a box count or a customer count.`
          : 'Set a number of boxes, a customer count, or both.',
        'danger',
      );
      return;
    }

    setBusy(true);
    const done: string[] = [];
    const failed: string[] = [];
    try {
      for (const j of jobs) {
        try {
          await api.post('/api/field/targets', {
            ...(who.kind === 'driver' ? { driverId: who.id } : { salespersonId: who.id }),
            // Monthly only. The day is the month ÷ 30, worked out on read —
            // a stored daily row would stop agreeing with the month the
            // first time somebody edited it.
            periodType: 'monthly',
            periodMonth: month,
            dimensionType: j.categoryId ? 'category' : 'overall',
            ...(j.categoryId ? { categoryId: j.categoryId } : {}),
            targetBoxes: j.boxes,
            targetCustomerCount: j.cust,
            ...(notes.trim() ? { notes: notes.trim() } : {}),
          });
          done.push(j.name);
        } catch (err) {
          failed.push(`${j.name}: ${err instanceof Error ? err.message : 'failed'}`);
        }
      }

      /**
       * Both halves reported. "Saved" after three of five went through would
       * send the admin back to set all five again — and the two that DID
       * work would then exist twice, because the server adds rather than
       * replaces.
       */
      if (failed.length === 0) {
        toast.show(done.length > 1 ? `${done.length} targets set.` : 'Target set.', 'ok');
        await onDone();
      } else {
        toast.show(
          `${done.length} set. Not set — ${failed.join('; ')}`,
          'danger',
        );
        if (done.length > 0) await onDone();
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onClose={onClose}
      title="Set a target"
      footer={
        <>
          <Button variant="ghost" block onClick={onClose} disabled={busy}>{t('app.cancel')}</Button>
          <Button block busy={busy} onClick={() => void submit()}>{t('app.save')}</Button>
        </>
      }
    >
      <div className="stack-2">
        {/*
          MONTHLY ONLY. The daily/monthly segment is gone.

          6 Oct: "daily target be automatically be divided by 30 days". A
          daily figure that can be typed is a second number to keep in step
          with the month, and it would be wrong the first time the month
          changed. The server refuses a daily target outright now.
        */}
        <TextInput label="Month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <div className="t-xs t-muted">
          The daily target is this ÷ 30 and appears on their phone by itself.
        </div>

        {/*
          ONE CONTROL, ALWAYS VISIBLE.

          This was a segment — "Overall" / "By category" — with the category
          picker appearing only after the second was tapped. The capability
          was fully built and reported missing on 3 Oct by somebody looking
          straight at the segment: two words on a toggle do not read as
          "this is where you choose a product group".

          A single list whose first entry is "All categories" says the same
          thing in one control, shows what the choices ARE without a tap, and
          removes a state where the form can be half-answered.
        */}
        {/*
          CATEGORIES AS TOGGLES, NOT A DROPDOWN.

          It was a single <select>, so setting a quarter's targets meant
          reopening this sheet once per product group and retyping the
          salesperson and the period each time — reported 4 Oct.

          Chips show every choice at once and let several be on together.
          Picking none is still the ordinary case and means one overall
          target, which is why there is no "All" chip to remember to tap.
        */}
        <div>
          <div className="t-xs t-muted" style={{ marginBottom: 6 }}>
            Categories · tap to include. None selected = one overall target.
          </div>
          {(categories.data?.items.length ?? 0) === 0 ? (
            <Banner tone="warn">
              <span className="t-sm">
                No categories have come across from Business Central yet. Run
                &quot;Sync master data&quot; on the Business Central screen.
              </span>
            </Banner>
          ) : (
            <div className="row-wrap" style={{ gap: 6 }}>
              {(categories.data?.items ?? []).map((cat) => {
                const on = picked[cat.id] !== undefined;
                return (
                  <button
                    key={cat.id}
                    type="button"
                    aria-pressed={on}
                    style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                    onClick={() => setPicked((prev) => {
                      const next = { ...prev };
                      if (on) delete next[cat.id];
                      else next[cat.id] = { boxes: '', customers: '' };
                      return next;
                    })}
                  >
                    {/* The product count, because a category holding two
                        items is a target worth thinking twice about. */}
                    <Chip tone={on ? 'ok' : undefined}>
                      {cat.name} ({cat.product_count})
                    </Chip>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/*
          A FIGURE PER CATEGORY.

          Not one amount applied to all of them: a distributor almost never
          wants the same rupees on water as on energy drinks, and a form that
          made that the easy path would get it typed that way.
        */}
        {chosen.map((id) => {
          const cat = categories.data?.items.find((c) => c.id === id);
          return (
            <Card key={id} className="stack-2">
              <div className="t-sm t-semi">{cat?.name ?? 'Category'}</div>
              <TextInput
                label="Boxes this month"
                numeric
                value={picked[id]!.boxes}
                onChange={(e) => setPicked((p2) => ({
                  ...p2, [id]: { ...p2[id]!, boxes: e.target.value.replace(/\D/g, '') },
                }))}
              />
              <TextInput
                label="Customers to bill"
                numeric
                value={picked[id]!.customers}
                onChange={(e) => setPicked((p2) => ({
                  ...p2, [id]: { ...p2[id]!, customers: e.target.value.replace(/\D/g, '') },
                }))}
              />
            </Card>
          );
        })}

        {chosen.length === 0 && (
          <TextInput
            label="Boxes this month"
            numeric
            value={boxes}
            onChange={(e) => setBoxes(e.target.value.replace(/\D/g, ''))}
            hint="A box of 24 and a box of 30 each count as one."
          />
        )}
        {/*
          CARTONS REMOVED, 3 Oct: "there is no need of carton section, you can
          remove this."

          It was one of three measures a target could carry. The column stays
          — targets already set with a carton figure are real records and
          deleting the column would rewrite history — but nothing sets it any
          more and nothing displays it, because a measure that cannot be set
          and still appears on a progress bar is worse than no measure.
        */}
        {chosen.length === 0 && (
          <TextInput
            label="Customers to bill"
            numeric
            value={customers}
            onChange={(e) => setCustomers(e.target.value.replace(/\D/g, ''))}
            hint="Counts distinct customers ordering in the period"
          />
        )}
        <TextArea
          label={t('app.notesOptional')}
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <div className="t-xs t-muted">
          {chosen.length > 1
            ? `Saves ${chosen.length} separate targets, one per category.`
            : 'Setting a target for a period that already has one adds a second target '
              + 'rather than replacing it — check the list before saving.'}
        </div>
      </div>
    </Sheet>
  );
}
